// Filename: CookieConsentModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Shield, BarChart, Target, Users } from 'lucide-react';

const CookieConsentModal = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [showCustomize, setShowCustomize] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, can't be disabled
        analytics: false,
        marketing: false,
        functional: false
    });

    useEffect(() => {
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Show modal after a short delay
            setTimeout(() => setIsVisible(true), 1000);
        }
    }, []);

    const handleAcceptAll = () => {
        const allAccepted = {
            necessary: true,
            analytics: true,
            marketing: true,
            functional: true
        };
        localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
        setIsVisible(false);
        // Here you would implement actual cookie/analytics initialization
        console.log('All cookies accepted:', allAccepted);
    };

    const handleRejectAll = () => {
        const onlyNecessary = {
            necessary: true,
            analytics: false,
            marketing: false,
            functional: false
        };
        localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
        setIsVisible(false);
        console.log('Only necessary cookies accepted:', onlyNecessary);
    };

    const handleCustomize = () => {
        setShowCustomize(true);
    };

    const handleSavePreferences = () => {
        localStorage.setItem('cookieConsent', JSON.stringify(preferences));
        setIsVisible(false);
        console.log('Custom preferences saved:', preferences);
        // Here you would implement actual cookie/analytics initialization based on preferences
    };

    const handlePreferenceChange = (type) => {
        if (type === 'necessary') return; // Can't disable necessary cookies
        
        setPreferences(prev => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
            
            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                {!showCustomize ? (
                    // Initial consent modal
                    <div className="p-6 sm:p-8">
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center">
                                <Shield className="w-8 h-8 text-red-500 mr-3" />
                                <h2 className="text-2xl font-bold text-gray-900">We Value Your Privacy</h2>
                            </div>
                        </div>
                        
                        <div className="mb-6">
                            <p className="text-gray-700 leading-relaxed mb-4">
                                We use cookies and similar technologies to enhance your browsing experience, 
                                analyse site traffic, and understand where our visitors are coming from. 
                                By clicking "Accept All", you consent to our use of cookies.
                            </p>
                            <p className="text-gray-600 text-sm">
                                You can customise your preferences or learn more in our{' '}
                                <a href="/privacy-policy" className="text-red-500 hover:text-red-600 underline">
                                    Privacy Policy
                                </a>{' '}
                                and{' '}
                                <a href="/cookie-policy" className="text-red-500 hover:text-red-600 underline">
                                    Cookie Policy
                                </a>.
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleAcceptAll}
                                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex-1"
                            >
                                Accept All
                            </button>
                            <button
                                onClick={handleRejectAll}
                                className="border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex-1"
                            >
                                Reject All
                            </button>
                            <button
                                onClick={handleCustomize}
                                className="border-2 border-red-500 text-red-500 hover:bg-red-50 px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex-1"
                            >
                                Customise
                            </button>
                        </div>
                    </div>
                ) : (
                    // Customization modal
                    <div className="p-6 sm:p-8">
                        <div className="flex items-start justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-900">Cookie Preferences</h2>
                            <button
                                onClick={() => setShowCustomize(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <p className="text-gray-700 mb-6">
                            Choose which types of cookies you want to allow. Your choices will be saved and can be changed at any time.
                        </p>

                        <div className="space-y-6 mb-8">
                            {/* Necessary Cookies */}
                            <div className="flex items-start space-x-4 p-4 bg-gray-50 rounded-lg">
                                <Shield className="w-6 h-6 text-gray-600 mt-1 flex-shrink-0" />
                                <div className="flex-grow">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">Necessary Cookies</h3>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={true}
                                                disabled={true}
                                                className="w-5 h-5 text-red-500 border-gray-300 rounded focus:ring-red-500 opacity-50"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Essential for the website to function properly. These cannot be disabled.
                                    </p>
                                </div>
                            </div>

                            {/* Analytics Cookies */}
                            <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                                <BarChart className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                                <div className="flex-grow">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">Analytics Cookies</h3>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={preferences.analytics}
                                                onChange={() => handlePreferenceChange('analytics')}
                                                className="w-5 h-5 text-red-500 border-gray-300 rounded focus:ring-red-500"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Help us understand how visitors interact with our website by collecting anonymous information.
                                    </p>
                                </div>
                            </div>

                            {/* Marketing Cookies */}
                            <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                                <Target className="w-6 h-6 text-green-600 mt-1 flex-shrink-0" />
                                <div className="flex-grow">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">Marketing Cookies</h3>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={preferences.marketing}
                                                onChange={() => handlePreferenceChange('marketing')}
                                                className="w-5 h-5 text-red-500 border-gray-300 rounded focus:ring-red-500"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Used to track visitors across websites to display relevant advertisements and measure campaign effectiveness.
                                    </p>
                                </div>
                            </div>

                            {/* Functional Cookies */}
                            <div className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg">
                                <Users className="w-6 h-6 text-purple-600 mt-1 flex-shrink-0" />
                                <div className="flex-grow">
                                    <div className="flex items-center justify-between mb-2">
                                        <h3 className="font-semibold text-gray-900">Functional Cookies</h3>
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                checked={preferences.functional}
                                                onChange={() => handlePreferenceChange('functional')}
                                                className="w-5 h-5 text-red-500 border-gray-300 rounded focus:ring-red-500"
                                            />
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        Enable enhanced functionality like live chat, social media features, and personalised content.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            <button
                                onClick={handleSavePreferences}
                                className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex-1"
                            >
                                Save Preferences
                            </button>
                            <button
                                onClick={() => setShowCustomize(false)}
                                className="border-2 border-gray-300 hover:border-gray-400 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-full font-semibold transition-colors duration-200 flex-1"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CookieConsentModal;